/**
 * Advisory value-format checks.
 *
 * ADVISORY IS THE POINT. A format mismatch produces a WARNING string, never a
 * validation error and never a dropped entry:
 *
 *  - `parseTechnicalIdentifiers` runs over untrusted stored jsonb. If a format
 *    check could fail there, one legacy row with a hand-noted "tenant id" like
 *    "see the M365 admin portal" would silently vanish from the company on the
 *    next read — data loss caused by a lint rule.
 *  - The write paths (CRM editor, Meridian tool, Slack modal) are staffed by
 *    engineers recording what a vendor actually gave them. A vendor that emits
 *    an id in a shape we did not anticipate is our gap, not the engineer's.
 *
 * So: shape checks inform, they never block. Surface the warning next to the
 * field and let the human decide.
 */

import { findFieldDef, systemDisplay } from "./registry";
import type { TechnicalIdentifierFormat } from "./systems";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DOMAIN = /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/i;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DIGITS = /^\d+$/;
const HEX32 = /^[0-9a-f]{32}$/i;
const AWS_ACCOUNT = /^\d{12}$/;
const GOOGLE_CUSTOMER = /^C[0-9a-z]{6,}$/i;
const GOOGLE_ADS_CUSTOMER = /^\d{3}-\d{3}-\d{4}$|^\d{10}$/;
const GCP_PROJECT = /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/;
const STRIPE_ACCOUNT = /^acct_[A-Za-z0-9]+$/;

/** What each format expects, phrased for a human reading it under an input. */
const EXPECTATION: Record<TechnicalIdentifierFormat, string> = {
  text: "any text",
  uuid: "a UUID (8-4-4-4-12 hex)",
  domain: "a domain name",
  email: "an email address",
  url: "an http(s) URL",
  digits: "digits only",
  hex32: "32 hexadecimal characters",
  aws_account_id: "a 12-digit AWS account number",
  google_customer_id: 'a Workspace customer id (starts with "C")',
  google_ads_customer_id: "a Google Ads customer id (123-456-7890)",
  gcp_project_id: "a GCP project id (lowercase, 6-30 chars)",
  stripe_account_id: 'a Stripe account id (starts with "acct_")',
};

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/** Does `value` match the syntactic shape of `format`? */
export function matchesFormat(format: TechnicalIdentifierFormat, value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  switch (format) {
    case "text":
      return true;
    case "uuid":
      return UUID.test(v);
    case "domain":
      return DOMAIN.test(v);
    case "email":
      return EMAIL.test(v);
    case "url":
      return isHttpUrl(v);
    case "digits":
      return DIGITS.test(v);
    case "hex32":
      return HEX32.test(v);
    case "aws_account_id":
      return AWS_ACCOUNT.test(v);
    case "google_customer_id":
      return GOOGLE_CUSTOMER.test(v);
    case "google_ads_customer_id":
      return GOOGLE_ADS_CUSTOMER.test(v);
    case "gcp_project_id":
      return GCP_PROJECT.test(v);
    case "stripe_account_id":
      return STRIPE_ACCOUNT.test(v);
  }
}

/**
 * Human-readable warning when a value does not look like the shape its
 * (system, label) declares. `null` when it matches, when the system declares no
 * such field, or when the field is freeform text — i.e. `null` means "nothing
 * to say", never "validated".
 */
export function describeValueIssue(
  system: string,
  label: string,
  value: string,
): string | null {
  const field = findFieldDef(system, label);
  if (!field || field.format === "text") return null;
  if (matchesFormat(field.format, value)) return null;
  return `${systemDisplay(system)} ${field.label} is usually ${
    EXPECTATION[field.format]
  }. Saved as entered — double-check the value.`;
}
